export type DiffTurn = { name: string; is_placeholder: boolean; text: string; start_ms: number; end_ms: number }

export type Focus = 'speakers' | 'words'

// Units picked on transcript differences are judged on words; everything else is judged on speakers.
export const focusOf = (unit: { picked_by: string }): Focus => (unit.picked_by === 'wer' ? 'words' : 'speakers')

// Case and punctuation never count as a difference, so "Friday?" matches "friday".
const NON_WORD = new RegExp('[^\\p{L}\\p{N}]', 'gu')
export const normalizeWord = (word: string): string => word.toLowerCase().replace(NON_WORD, '')

export const splitWords = (text: string): string[] => text.split(/\s+/).filter(Boolean)

export type SideDiff = {
  // One entry per turn, one flag per whitespace token: true when the word has no counterpart on the other side.
  a: boolean[][]
  b: boolean[][]
}

// LCS over the words of every turn on each side, so a turn split in two on one side still lines up.
export function diffTurns(a: readonly DiffTurn[], b: readonly DiffTurn[]): SideDiff {
  const wordsA = a.map((turn) => splitWords(turn.text))
  const wordsB = b.map((turn) => splitWords(turn.text))
  const flatA = wordsA.flat().map(normalizeWord)
  const flatB = wordsB.flat().map(normalizeWord)
  const n = flatA.length
  const m = flatB.length
  const same = (i: number, j: number) => flatA[i] !== '' && flatA[i] === flatB[j]

  const table: Uint32Array[] = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1))
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      table[i][j] = same(i, j) ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1])
    }
  }

  const unmatchedA = new Array<boolean>(n).fill(false)
  const unmatchedB = new Array<boolean>(m).fill(false)
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (same(i, j)) {
      i++
      j++
    } else if (table[i + 1][j] >= table[i][j + 1]) unmatchedA[i++] = true
    else unmatchedB[j++] = true
  }
  while (i < n) unmatchedA[i++] = true
  while (j < m) unmatchedB[j++] = true

  const regroup = (words: string[][], flags: boolean[]): boolean[][] => {
    let offset = 0
    return words.map((turnWords) => {
      const slice = flags.slice(offset, offset + turnWords.length)
      offset += turnWords.length
      return slice
    })
  }
  return { a: regroup(wordsA, unmatchedA), b: regroup(wordsB, unmatchedB) }
}

const overlaps = (x: DiffTurn, y: DiffTurn) => Math.min(x.end_ms, y.end_ms) - Math.max(x.start_ms, y.start_ms) > 0

// A name chip is outlined when its name differs from a time-overlapping turn on the other side. Two placeholders
// ("Speaker 3" vs "Speaker 4") are the same "unnamed" claim; with no overlapping turn, only a real name is flagged.
export function nameDiffers(turn: DiffTurn, others: readonly DiffTurn[]): boolean {
  const counterparts = others.filter((other) => overlaps(turn, other))
  if (counterparts.length === 0) return !turn.is_placeholder
  return counterparts.some((other) => other.name !== turn.name && !(other.is_placeholder && turn.is_placeholder))
}
